// Shared, non-fabricating helpers for rendering a backend `dataset_summary`
// payload — used both inside chat (MessageBubble) and on the pre-chat
// Upload Analysis screen, so there is exactly one implementation.

// Normalizes a string / string[] / {message|text|detail}[] / single object
// into a flat array of displayable strings, without altering their content.
export function toMessageList(x) {
  if (!x) return [];
  if (Array.isArray(x)) {
    return x
      .map(item => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return item.message ?? item.text ?? item.detail ?? '';
        return item != null ? String(item) : '';
      })
      .filter(Boolean);
  }
  if (typeof x === 'string') return [x];
  if (typeof x === 'object') {
    const single = x.message ?? x.text ?? x.detail ?? '';
    return single ? [single] : [];
  }
  return [];
}

function statValue(v) {
  if (v === undefined || v === null) return null;
  return Array.isArray(v) ? v.length : v;
}

// Reads the backend's own dataset_summary values defensively (flat or
// aliased field names) — labels them, never invents them.
export function pickDatasetFields(ds) {
  if (!ds || typeof ds !== 'object') return null;

  const name = ds.name ?? ds.dataset_name;
  const rows = statValue(ds.rows ?? ds.row_count);
  const columns = statValue(ds.columns ?? ds.column_count);
  const worksheets = statValue(ds.worksheets ?? ds.sheets);
  const tables = statValue(ds.tables ?? ds.table_count);
  const quality = ds.quality_score ?? ds.quality ?? ds.score;
  const detectedTypes = ds.detected_types ?? ds.column_types ?? ds.dtypes;
  const recommendations = toMessageList(ds.recommendations);

  return { name, rows, columns, worksheets, tables, quality, detectedTypes, recommendations };
}

const CURRENCY_KEYWORDS = ['currency', 'money'];
const NUMERIC_KEYWORDS = ['int', 'num', 'float', 'double', 'decimal'];
const DATE_KEYWORDS = ['date', 'time'];
const TEXT_KEYWORDS = ['text', 'char', 'string', 'str'];
const BOOL_KEYWORDS = ['bool'];

// Buckets each column's backend-provided type string into a display
// category — the same kind of keyword categorization RightPanel already
// does per-column (typeColor/typeAbbr), just aggregated into counts.
export function categorizeFieldTypes(detectedTypes) {
  if (!detectedTypes || typeof detectedTypes !== 'object' || Array.isArray(detectedTypes)) return [];

  const counts = { Currency: 0, Numeric: 0, Date: 0, Text: 0, Boolean: 0, Other: 0 };
  Object.values(detectedTypes).forEach(rawType => {
    const t = String(rawType || '').toLowerCase();
    if (BOOL_KEYWORDS.some(k => t.includes(k))) counts.Boolean += 1;
    else if (CURRENCY_KEYWORDS.some(k => t.includes(k))) counts.Currency += 1;
    else if (NUMERIC_KEYWORDS.some(k => t.includes(k))) counts.Numeric += 1;
    else if (DATE_KEYWORDS.some(k => t.includes(k))) counts.Date += 1;
    else if (TEXT_KEYWORDS.some(k => t.includes(k))) counts.Text += 1;
    else counts.Other += 1;
  });

  return Object.entries(counts)
    .filter(([, count]) => count > 0)
    .map(([label, count]) => ({ label, count }));
}
